import { APP_DEPLOY_URL } from "@/lib/site-config";

export interface NavItem {
  label: string;
  href: string;
  /** One-line supporting copy shown under the label in the mega menu. */
  description?: string;
  /** Icon key resolved in MegaMenu's ICONS map (lucide icon). */
  icon?: string;
  /** Image logo path (e.g. chain logos). Takes precedence over `icon`. */
  logoSrc?: string;
  /** Optional pill, e.g. "New" or "Soon". */
  badge?: string;
  /** Nested sub-links, rendered indented beneath the item (e.g. app CTAs). */
  children?: NavItem[];
}

/** A titled column of items inside a mega menu. */
export interface NavSection {
  title: string;
  items: NavItem[];
}

/** A link rendered in the mega menu footer bar. */
export interface NavFooterLink {
  label: string;
  href: string;
}

/** Compact promo/CTA rail shown on the right of a mega menu. */
export interface NavCta {
  eyebrow?: string;
  title: string;
  description?: string;
  buttonLabel: string;
  href: string;
  external?: boolean;
}

export interface NavGroup {
  label: string;
  /** Eyebrow heading shown above a flat `items`/`logos` grid (layouts without
   *  `sections`, which carry their own per-column titles). */
  heading?: string;
  /** Categorised columns. Preferred layout — renders a multi-column panel. */
  sections?: NavSection[];
  /** Flat list — renders a grid with `columns` tracks. Used when there are no categories. */
  items?: NavItem[];
  /** Grid column count for a flat `items` list. Defaults to 2. */
  columns?: number;
  /** "list" (icon + label + description rows) | "logos" (compact centred logo tiles). */
  layout?: "list" | "logos";
  /** Panel width in px. Defaults are derived per layout when omitted. */
  width?: number;
  /** Right-hand CTA rail. */
  cta?: NavCta;
  /** Links shown in the footer bar at the bottom of the panel. */
  footer?: NavFooterLink[];
  viewAllHref?: string;
  viewAllLabel?: string;
}

export interface NavConfig {
  mainLinks: NavItem[];
  megaMenu: Record<string, NavGroup>;
  ctaLinks: NavItem[];
}

// NOTE: Nav items whose destination pages don't exist yet (Docs, Case Studies,
// Security & Compliance, Staking calculator) are commented out below rather than
// left as dead `href: "#"` links. Uncomment and add the real href when ready.
// The top-level mainLinks entries with `href: "#"` (Services, Use Cases,
// Resources) are mega-menu triggers — they open a panel and never navigate, so
// they intentionally keep the placeholder href.
export const navigation: NavConfig = {
  mainLinks: [
    { label: "Services",   href: "#" },
    { label: "Chains",     href: "/chains-ecosystem" },
    { label: "Use Cases",  href: "#" },
    { label: "Resources",  href: "#" },
    { label: "Cloud Hosting", href: "/cloud-hosting" },
    { label: "Pricing",    href: "/pricing" },
  ],
  megaMenu: {
    Services: {
      label: "Services",
      width: 860,
      sections: [
        {
          title: "Developers",
          items: [
            {
              label: "Managed RPC Endpoints",
              href: "/solutions/rpc-api",
              description: "Production RPC across 50+ chains",
              icon: "zap",
            },
            {
              label: "Node as a Service",
              href: "/solutions/node-as-a-service",
              description: "Manage full and archive nodes",
              icon: "database",
            },
            {
              label: "Rollup as a Service",
              href: "/solutions/rollup-as-a-service",
              description: "Launch your own L2 / L3",
              icon: "layers",
            },
            {
              label: "Appchain Builder",
              href: "/solutions/appchain",
              description: "Purpose-built application chains",
              icon: "boxes",
            },
            {
              label: "Validator Node Hosting",
              href: "/solutions/validator-nodes",
              description: "MEV-optimized, non-custodial staking",
              icon: "shield",
              children: [
                { label: "Buy a Validator Node", href: "/solutions/validator-node-buy-online" },
                // Commented out — no destination page yet (href "#").
                // { label: "Staking rewards calculator", href: "#" },
              ],
            },
            // Commented out — dVPN kept off the menu; page stays live via sitemap.
            // {
            //   label: "dVPN Node Hosting",
            //   href: "/solutions/dvpn",
            //   description: "Sentinel dVPN nodes, metered and monitored",
            //   icon: "network",
            // },
          ],
        },
        {
          title: "Enterprise & Institutions",
          items: [
            {
              label: "Dedicated Clusters",
              href: "/solutions/dedicated-clusters",
              description: "Isolated, high-throughput backends",
              icon: "network",
            },
            {
              label: "Enterprise Blockchain",
              href: "/solutions/enterprise-blockchain",
              description: "Permissioned networks for institutions",
              icon: "building",
            },
            {
              label: "Web3 Infrastructure Provider",
              href: "/solutions/web3-infrastructure-provider",
              description: "The full stack under one contract",
              icon: "server",
            },
          ],
        },
      ],
      cta: {
        eyebrow: "Start Free",
        title: "600K requests / month",
        description: "Spin up your free RPC endpoint in seconds. No card needed.",
        buttonLabel: "Get Your Free Endpoint",
        href: APP_DEPLOY_URL,
        external: true,
      },
      footer: [
        { label: "Talk to an expert", href: "/contact-us" },
      ],
    },
    Chains: {
      label: "Chains",
      heading: "Featured Chains",
      layout: "logos",
      columns: 4,
      width: 620,
      items: [
        { label: "Ethereum",        href: "/chains/ethereum",           logoSrc: "/images/chains/ethereum.svg" },
        { label: "Bitcoin",         href: "/chains/bitcoin-nodes",      logoSrc: "/images/chains/bitcoin.svg" },
        { label: "Solana",          href: "/chains/solana",             logoSrc: "/images/chains/solana.svg" },
        { label: "BNB Smart Chain", href: "/chains/bsc-nodes",          logoSrc: "/images/chains/bsc.svg" },
        { label: "Polygon",         href: "/chains/polygon-nodes",      logoSrc: "/images/chains/polygon.png" },
        { label: "Base",            href: "/chains/base-rpc-nodes",     logoSrc: "/images/chains/base.png" },
        { label: "Avalanche",       href: "/chains/avalanche-rpc-node", logoSrc: "/images/chains/avalanche.png" },
        { label: "Polkadot",        href: "/chains/polkadot-nodes",     logoSrc: "/images/chains/polkadot.png" },
      ],
      viewAllHref: "/chains-ecosystem",
      viewAllLabel: "View All 50+ Chains",
    },
    "Use Cases": {
      label: "Use Cases",
      width: 640,
      sections: [
        {
          title: "By use case",
          items: [
            {
              label: "DeFi & Trading",
              href: "/use-cases/defi-trading",
              description: "Low-latency infra for DeFi & trading",
              icon: "chart",
            },
            {
              label: "AI Agents",
              href: "/use-cases/ai-agents",
              description: "Agent-ready infrastructure",
              icon: "bot",
              badge: "New",
            },
            {
              label: "Wallets & Payments",
              href: "/use-cases/crypto-wallet",
              description: "Instant reads, stablecoin rails",
              icon: "wallet",
            },
            {
              label: "Post-Quantum Security",
              href: "/use-cases/post-quantum-security",
              description: "Quantum-safe accounts, deployable today",
              icon: "shield",
            },
          ],
        },
        {
          title: "By organization",
          items: [
            {
              label: "Exchanges",
              href: "/use-cases/crypto-exchange",
              description: "High-throughput infra for exchanges",
              icon: "swap",
            },
            {
              label: "Enterprise & Institutions",
              href: "/use-cases/enterprise-blockchain-infrastructure",
              description: "Compliant infra for institutions",
              icon: "building",
            },
            {
              label: "Startups & Builders",
              href: "/use-cases/web3-startups",
              description: "Scale-ready infra for builders",
              icon: "rocket",
            },
          ],
        },
      ],
      footer: [{ label: "Talk to an expert", href: "/contact-us" }],
    },
    Resources: {
      label: "Resources",
      width: 640,
      sections: [
        {
          title: "Learn",
          items: [
            {
              label: "Docs",
              href: "/docs",
              description: "Guides, references & API",
              icon: "book",
            },
            {
              label: "Blog & Guides",
              href: "/blogs",
              description: "Product news and how-tos",
              icon: "news",
            },
            // Commented out — no destination page yet (href "#").
            // {
            //   label: "Case Studies",
            //   href: "#",
            //   description: "How teams build on Instanodes",
            //   icon: "award",
            // },
          ],
        },
        {
          title: "Trust",
          items: [
            {
              label: "System Status",
              href: "/node-status",
              description: "Live uptime & incidents",
              icon: "activity",
              // badge: "Live",
            },
            // Commented out — no destination page yet (href "#").
            // {
            //   label: "Security & Compliance",
            //   href: "#",
            //   description: "SOC 2, SLAs & data residency",
            //   icon: "shield",
            // },
            {
              label: "Compare Providers",
              href: "/blogs/instanodes-vs-other-node-providers-what-sets-us-apart",
              description: "vs. QuickNode, Alchemy & AllNodes",
              icon: "compare",
            },
            {
              label: "Referral Program",
              href: "/refer-and-earn",
              description: "Refer protocols, earn rewards",
              icon: "gift",
            },
          ],
        },
      ],
    },
  },
  ctaLinks: [
    { label: "Contact Us",  href: "/contact-us" },
  ],
};
